import React, { useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

const OTPInput = ({ length = 6, value, onChange, onComplete, className }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, digitValue) => {
    // Only allow single digit
    const digit = digitValue.slice(-1);
    
    if (!/^[0-9]$/.test(digit) && digit !== '') return;

    const newValue = value.split('');
    newValue[index] = digit;
    const newValueString = newValue.join('');
    
    onChange(newValueString);

    // Auto-advance to next box
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if complete
    if (newValueString.length === length && !newValueString.includes('')) {
      onComplete?.(newValueString);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length);
    if (/^[0-9]+$/.test(pastedData)) {
      onChange(pastedData.padEnd(length, ''));
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className={cn('flex gap-2', className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={cn(
            'w-12 h-14 text-center',
            'glass-subtle rounded-[var(--radius-md)]',
            'font-["DM_Mono"] text-[22px]',
            'transition-all duration-180',
            'focus:outline-none focus:ring-2',
            value[index] 
              ? 'border-[rgba(61,90,128,0.3)] bg-[rgba(61,90,128,0.04)]'
              : 'border-[rgba(255,255,255,0.60)]',
            'focus:border-[rgba(212,120,74,0.4)] focus:ring-[rgba(212,120,74,0.1)]'
          )}
          style={{ color: 'var(--color-text-primary)' }}
          data-testid={`otp-input-${index}`}
        />
      ))}
    </div>
  );
};

OTPInput.displayName = 'OTPInput';

export { OTPInput };